import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RichTextEditor } from "../components/RichTextEditor";
import FileAttachments from "../components/FileAttachments";
import ScheduleCallDialog from "../components/ScheduleCallDialog";
import NotesLog from "../components/NotesLog";
import CategoryEditor from "../components/CategoryEditor";
import SocialLinks from "../components/SocialLinks";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  Globe, 
  MapPin, 
  Edit2, 
  Trash2,
  Clock,
  Star,
  CheckCircle2,
  XCircle,
  Voicemail,
  PhoneMissed,
  Send,
  FileText,
  CalendarPlus,
  ArrowRight,
  Loader2,
  ChevronRight,
  Sparkles,
  UserCircle,
  Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  prospect: "status-prospect",
  contacted: "status-contacted",
  qualified: "status-qualified",
  closed_won: "status-closed-won",
  closed_lost: "status-closed-lost"
};

const statusLabels = {
  prospect: "Prospect",
  contacted: "Contacted",
  qualified: "Qualified",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost"
};

const callOutcomes = [
  { value: "answered", label: "Answered", icon: CheckCircle2, color: "text-emerald-600" },
  { value: "no_answer", label: "No Answer", icon: PhoneMissed, color: "text-slate-500" },
  { value: "voicemail", label: "Voicemail", icon: Voicemail, color: "text-blue-500" },
  { value: "busy", label: "Busy", icon: Clock, color: "text-amber-500" },
  { value: "wrong_number", label: "Wrong Number", icon: XCircle, color: "text-red-500" },
  { value: "sent_email", label: "Sent Email", icon: Mail, color: "text-sky-500" }
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [calls, setCalls] = useState([]);
  const [emails, setEmails] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [showPostCallActions, setShowPostCallActions] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [callForm, setCallForm] = useState({
    outcome: "",
    notes: "",
    duration_seconds: ""
  });
  const [emailForm, setEmailForm] = useState({
    subject: "",
    body: "",
    template_id: null,
    to_emails: [],   // checkbox-selected recipients (subset of the lead's contact emails)
    cc: "",          // comma-separated UI value; parsed before send
    bcc: "",
  });
  const [emailTemplates, setEmailTemplates] = useState({ templates: [], custom: [] });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [nextLead, setNextLead] = useState(null);
  const [loadingNextLead, setLoadingNextLead] = useState(false);

  useEffect(() => {
    fetchLead();
    fetchCalls();
    fetchEmails();
    fetchUsers();
    fetchEmailTemplates();
  }, [leadId]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/leads/${leadId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
        setEditForm(data);
      } else if (response.status === 404) {
        toast.error("Lead not found");
        navigate("/leads");
      }
    } catch (error) {
      console.error("Failed to fetch lead:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalls = async () => {
    try {
      const response = await fetch(`${API}/leads/${leadId}/calls`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCalls(data);
      }
    } catch (error) {
      console.error("Failed to fetch calls:", error);
    }
  };

  const fetchEmails = async () => {
    try {
      const response = await fetch(`${API}/leads/${leadId}/emails`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setEmails(data);
      }
    } catch (error) {
      console.error("Failed to fetch emails:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${API}/email-templates`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data);
      }
    } catch (error) {
      console.error("Failed to fetch email templates:", error);
    }
  };

  const fetchNextLead = async () => {
    setLoadingNextLead(true);
    try {
      const response = await fetch(`${API}/leads/${leadId}/next`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setNextLead(data);
      }
    } catch (error) {
      console.error("Failed to fetch next lead:", error);
    } finally {
      setLoadingNextLead(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API}/leads/${leadId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        const updated = await response.json();
        setLead(updated);
        setIsEditing(false);
        toast.success("Lead updated");
      } else {
        toast.error("Failed to update lead");
      }
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      const response = await fetch(`${API}/leads/${leadId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Lead deleted");
        navigate("/leads");
      } else {
        toast.error("Failed to delete lead");
      }
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleLogCall = async () => {
    if (!callForm.outcome) {
      toast.error("Please select an outcome");
      return;
    }

    try {
      const response = await fetch(`${API}/leads/${leadId}/calls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...callForm,
          duration_seconds: callForm.duration_seconds ? parseInt(callForm.duration_seconds) : null
        })
      });

      if (response.ok) {
        toast.success("Call logged");
        fetchCalls();
        fetchLead();
        fetchNextLead();
        setShowPostCallActions(true);
      } else {
        toast.error("Failed to log call");
      }
    } catch (error) {
      toast.error("Failed to log call");
    }
  };

  const handleSelectTemplate = (template) => {
    // Replace placeholders with actual values
    let subject = template.subject;
    let body = template.body;
    
    const replacements = {
      "{{contact_name}}": lead?.contact_name || lead?.business_name?.split(" ")[0] || "there",
      "{{business_name}}": lead?.business_name || "your business",
      "{{sender_name}}": "Your Name", // Will be replaced by backend
      "{{company_name}}": "Your Company" // Could be fetched from org
    };
    
    Object.entries(replacements).forEach(([key, value]) => {
      subject = subject.replace(new RegExp(key, "g"), value);
      body = body.replace(new RegExp(key, "g"), value);
    });
    
    setEmailForm({
      subject,
      body,
      template_id: template.template_id || null
    });
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) {
      toast.error("Please fill in subject and body");
      return;
    }

    const availableEmails = [lead?.email, lead?.contact_email, lead?.contact2_email].filter(Boolean);
    let selectedTo = (emailForm.to_emails || []).filter((e) => availableEmails.includes(e));
    if (selectedTo.length === 0) {
      // Backward-compatible fallback: first available email if nothing checked
      if (availableEmails.length > 0) selectedTo = [availableEmails[0]];
    }
    if (selectedTo.length === 0) {
      toast.error("This lead has no email address");
      return;
    }

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const parseList = (s) => (s || "").split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
    const ccList = parseList(emailForm.cc);
    const bccList = parseList(emailForm.bcc);
    const invalid = [...ccList, ...bccList].filter((e) => !EMAIL_RE.test(e));
    if (invalid.length) {
      toast.error(`Invalid email in CC/BCC: ${invalid.join(", ")}`);
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${API}/leads/${leadId}/emails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subject: emailForm.subject,
          body: emailForm.body,
          template_id: emailForm.template_id,
          to_emails: selectedTo,
          cc: ccList,
          bcc: bccList,
        })
      });

      if (response.ok) {
        const recipientCount = selectedTo.length + ccList.length + bccList.length;
        toast.success(
          recipientCount === 1
            ? `Email sent to ${selectedTo[0]}`
            : `Email sent to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`,
        );
        setIsEmailDialogOpen(false);
        setEmailForm({ subject: "", body: "", template_id: null, to_emails: [], cc: "", bcc: "" });
        fetchEmails();
        fetchLead();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email", { duration: 8000 });
      }
    } catch (error) {
      toast.error("Failed to send email — network error");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleGoToNextLead = () => {
    if (nextLead) {
      setIsCallDialogOpen(false);
      setShowPostCallActions(false);
      setCallForm({ outcome: "", notes: "", duration_seconds: "" });
      navigate(`/leads/${nextLead.lead_id}`);
    }
  };

  const handleSetTask = () => {
    setIsCallDialogOpen(false);
    setShowPostCallActions(false);
    setCallForm({ outcome: "", notes: "", duration_seconds: "" });
    navigate(`/tasks?create=true&lead_id=${leadId}&lead_name=${encodeURIComponent(lead?.business_name || "")}`);
  };

  const handleSendEmailAfterCall = () => {
    setIsCallDialogOpen(false);
    setShowPostCallActions(false);
    setCallForm({ outcome: "", notes: "", duration_seconds: "" });
    setIsEmailDialogOpen(true);
  };

  const resetCallDialog = () => {
    setIsCallDialogOpen(false);
    setShowPostCallActions(false);
    setCallForm({ outcome: "", notes: "", duration_seconds: "" });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/leads")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                {lead.business_name}
              </h1>
              <Badge className={statusColors[lead.status]}>
                {statusLabels[lead.status]}
              </Badge>
              <CategoryEditor
                leadId={lead.lead_id}
                value={lead.category}
                onChange={(newCat) => setLead((l) => ({ ...l, category: newCat }))}
              />
            </div>
            <p className="text-slate-500 text-sm mt-1">
              {lead.city}, {lead.state} • Added {formatDate(lead.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Generate Intel Button */}
          <Button
            variant="outline"
            onClick={() => navigate(`/sales-intelligence?industry=${encodeURIComponent(lead.category || "")}&company=${encodeURIComponent(lead.business_name || "")}&location=${encodeURIComponent(`${lead.city || ""}, ${lead.state || ""}`)}`)}
            data-testid="generate-intel-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Intel
          </Button>

          {/* Email Button */}
          <Button 
            variant="outline" 
            onClick={() => setIsEmailDialogOpen(true)}
            disabled={!lead.email && !lead.contact_email && !lead.contact2_email}
            data-testid="send-email-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send Email
          </Button>

          {/* Twilio Click-to-Call */}
          <Button
            variant="default"
            onClick={async () => {
              const to = lead.phone || lead.contact_phone || lead.contact2_phone;
              if (!to) {
                toast.error("This lead has no phone number on file");
                return;
              }
              try {
                const res = await fetch(`${API}/twilio-voice/calls/initiate`, {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ lead_id: lead.lead_id, to_number: to }),
                });
                const data = await res.json();
                if (res.ok) {
                  toast.success(data.message || "Call initiated");
                } else {
                  toast.error(data.detail || "Failed to start call");
                }
              } catch {
                toast.error("Failed to start call");
              }
            }}
            disabled={!lead.phone && !lead.contact_phone && !lead.contact2_phone}
            data-testid="dial-twilio-btn"
          >
            <Phone className="w-4 h-4 mr-2" />
            Call
          </Button>

          {/* Schedule Call (reuses /api/appointments) */}
          <ScheduleCallDialog lead={lead} users={users} />

          {/* Log Call Button */}
          <Dialog open={isCallDialogOpen} onOpenChange={(open) => {
            if (!open) resetCallDialog();
            else setIsCallDialogOpen(true);
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="log-call-btn">
                <Phone className="w-4 h-4 mr-2" />
                Log Call
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {showPostCallActions ? "Call Logged - What's Next?" : "Log Call"}
                </DialogTitle>
              </DialogHeader>

              {!showPostCallActions ? (
                // Call logging form
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Call Outcome *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {callOutcomes.map((outcome) => {
                        const Icon = outcome.icon;
                        return (
                          <Button
                            key={outcome.value}
                            type="button"
                            variant={callForm.outcome === outcome.value ? "default" : "outline"}
                            className="justify-start"
                            onClick={() => setCallForm({ ...callForm, outcome: outcome.value })}
                            data-testid={`outcome-${outcome.value}`}
                          >
                            <Icon className={`w-4 h-4 mr-2 ${outcome.color}`} />
                            {outcome.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label>Duration (seconds)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 120"
                      value={callForm.duration_seconds}
                      onChange={(e) => setCallForm({ ...callForm, duration_seconds: e.target.value })}
                      data-testid="call-duration"
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      placeholder="Add notes about the call..."
                      value={callForm.notes}
                      onChange={(e) => setCallForm({ ...callForm, notes: e.target.value })}
                      rows={3}
                      data-testid="call-notes"
                    />
                  </div>

                  <Button onClick={handleLogCall} className="w-full" data-testid="save-call-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Save Call Log
                  </Button>

                  {(lead?.email || lead?.contact_email || lead?.contact2_email) && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setIsCallDialogOpen(false);
                        setIsEmailDialogOpen(true);
                      }}
                      data-testid="send-email-from-call-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email to {lead.email || lead.contact_email || lead.contact2_email}
                    </Button>
                  )}
                </div>
              ) : (
                // Post-call actions
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mb-4">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Call logged successfully!</span>
                    </div>
                  </div>

                  <p className="text-sm text-slate-600">Choose your next action:</p>

                  <div className="space-y-2">
                    {/* Next Lead Button */}
                    <Button
                      variant="outline"
                      className="w-full justify-between h-auto py-3"
                      onClick={handleGoToNextLead}
                      disabled={loadingNextLead || !nextLead}
                      data-testid="next-lead-btn"
                    >
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-primary" />
                        <div className="text-left">
                          <p className="font-medium">Call Next Lead</p>
                          {loadingNextLead ? (
                            <p className="text-xs text-slate-500">Loading...</p>
                          ) : nextLead ? (
                            <p className="text-xs text-slate-500">{nextLead.business_name}</p>
                          ) : (
                            <p className="text-xs text-slate-500">No more leads</p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5" />
                    </Button>

                    {/* Set Task Button */}
                    <Button
                      variant="outline"
                      className="w-full justify-between h-auto py-3"
                      onClick={handleSetTask}
                      data-testid="set-task-btn"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarPlus className="w-5 h-5 text-amber-500" />
                        <div className="text-left">
                          <p className="font-medium">Set Follow-up Task</p>
                          <p className="text-xs text-slate-500">Create a reminder for this lead</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5" />
                    </Button>

                    {/* Send Email Button */}
                    <Button
                      variant="outline"
                      className="w-full justify-between h-auto py-3"
                      onClick={handleSendEmailAfterCall}
                      disabled={!lead?.email && !lead?.contact_email && !lead?.contact2_email}
                      data-testid="send-email-after-call-btn"
                    >
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-blue-500" />
                        <div className="text-left">
                          <p className="font-medium">Send Email</p>
                          <p className="text-xs text-slate-500">
                            {lead?.email || lead?.contact_email || lead?.contact2_email || "No email available"}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={resetCallDialog}
                  >
                    Done
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit/Delete */}
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-edit-btn">Save</Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="icon" onClick={() => setIsEditing(true)}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="text-red-600" onClick={handleDelete}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Email to {lead.business_name}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="compose" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="compose">Compose</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
            </TabsList>

            <TabsContent value="compose" className="space-y-4 py-4">
              {(() => {
                const leadEmailOptions = [
                  { value: lead.email, label: "Business email", source: "email" },
                  { value: lead.contact_email, label: lead.contact_name ? `Primary (${lead.contact_name})` : "Primary contact", source: "contact_email" },
                  { value: lead.contact2_email, label: lead.contact2_name ? `Secondary (${lead.contact2_name})` : "Secondary contact", source: "contact2_email" },
                ].filter((opt) => !!opt.value);
                // Initialize default selection to the first available email if nothing chosen yet
                if (emailForm.to_emails.length === 0 && leadEmailOptions.length > 0) {
                  // Use a microtask to avoid setState during render warning
                  setTimeout(() => {
                    setEmailForm((f) => f.to_emails.length === 0 && leadEmailOptions.length > 0
                      ? { ...f, to_emails: [leadEmailOptions[0].value] } : f);
                  }, 0);
                }
                return (
                  <div>
                    <Label>To <span className="text-slate-400 text-xs font-normal">(select one or more)</span></Label>
                    {leadEmailOptions.length === 0 ? (
                      <p className="text-sm text-slate-500 mt-1">No email addresses on file for this lead.</p>
                    ) : (
                      <div className="mt-1 space-y-1.5 rounded-md border border-slate-200 p-2.5">
                        {leadEmailOptions.map((opt) => {
                          const checked = emailForm.to_emails.includes(opt.value);
                          return (
                            <label
                              key={opt.value}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1.5 py-1"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setEmailForm((f) => {
                                    const next = e.target.checked
                                      ? [...f.to_emails, opt.value]
                                      : f.to_emails.filter((v) => v !== opt.value);
                                    return { ...f, to_emails: next };
                                  });
                                }}
                                data-testid={`to-checkbox-${opt.source}`}
                              />
                              <span className="text-slate-500 text-xs w-24 shrink-0">{opt.label}</span>
                              <span className="text-slate-900 truncate">{opt.value}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <Label htmlFor="email-cc">Cc <span className="text-slate-400 text-xs font-normal">(optional, comma-separated)</span></Label>
                <Input
                  id="email-cc"
                  value={emailForm.cc}
                  onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                  placeholder="manager@acme.com, ops@acme.com"
                  data-testid="email-cc"
                />
              </div>

              <div>
                <Label htmlFor="email-bcc">Bcc <span className="text-slate-400 text-xs font-normal">(optional, comma-separated)</span></Label>
                <Input
                  id="email-bcc"
                  value={emailForm.bcc}
                  onChange={(e) => setEmailForm({ ...emailForm, bcc: e.target.value })}
                  placeholder="archive@yourcrm.com"
                  data-testid="email-bcc"
                />
              </div>

              <div>
                <Label>Subject *</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Enter email subject"
                  data-testid="email-subject"
                />
              </div>

              <div>
                <Label>Body *</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={emailForm.body}
                    onChange={(html) => setEmailForm({ ...emailForm, body: html })}
                    placeholder="Write your email..."
                  />
                </div>
              </div>

              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail || (!lead.email && !lead.contact_email && !lead.contact2_email)}
                className="w-full"
                data-testid="send-email-submit"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Email
              </Button>
            </TabsContent>

            <TabsContent value="templates" className="py-4">
              <div className="space-y-2">
                <Label className="text-sm text-slate-500">Select a template to start with:</Label>
                
                {/* Default templates */}
                {emailTemplates.templates?.map((template, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="w-full justify-start h-auto py-3"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <FileText className="w-4 h-4 mr-3 text-slate-400" />
                    <div className="text-left">
                      <p className="font-medium">{template.name}</p>
                      <p className="text-xs text-slate-500">{template.subject}</p>
                    </div>
                  </Button>
                ))}

                {/* Custom templates */}
                {emailTemplates.custom?.length > 0 && (
                  <>
                    <div className="border-t my-4 pt-4">
                      <Label className="text-sm text-slate-500">Custom templates:</Label>
                    </div>
                    {emailTemplates.custom.map((template) => (
                      <Button
                        key={template.template_id}
                        variant="outline"
                        className="w-full justify-start h-auto py-3"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <FileText className="w-4 h-4 mr-3 text-primary" />
                        <div className="text-left">
                          <p className="font-medium">{template.name}</p>
                          <p className="text-xs text-slate-500">{template.subject}</p>
                        </div>
                      </Button>
                    ))}
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Business Name</Label>
                    <Input
                      value={editForm.business_name || ""}
                      onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })}
                      data-testid="edit-business-name"
                    />
                  </div>

                  {/* Secondary Contact */}
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
                    <p className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
                      <UserCircle className="w-4 h-4" />
                      Primary Contact
                    </p>
                    <div>
                      <Label>Contact Name</Label>
                      <Input
                        value={editForm.contact_name || ""}
                        onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                        placeholder="e.g., John Smith"
                        data-testid="edit-contact-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Contact Email</Label>
                        <Input
                          type="email"
                          value={editForm.contact_email || ""}
                          onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                          placeholder="john@company.com"
                          data-testid="edit-contact-email"
                        />
                      </div>
                      <div>
                        <Label>Contact Phone</Label>
                        <Input
                          value={editForm.contact_phone || ""}
                          onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          data-testid="edit-contact-phone"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
                    <p className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
                      <UserCircle className="w-4 h-4" />
                      Secondary Contact
                    </p>
                    <div>
                      <Label>Contact Name</Label>
                      <Input
                        value={editForm.contact2_name || ""}
                        onChange={(e) => setEditForm({ ...editForm, contact2_name: e.target.value })}
                        placeholder="e.g., Jane Doe"
                        data-testid="edit-contact2-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Contact Email</Label>
                        <Input
                          type="email"
                          value={editForm.contact2_email || ""}
                          onChange={(e) => setEditForm({ ...editForm, contact2_email: e.target.value })}
                          placeholder="jane@company.com"
                          data-testid="edit-contact2-email"
                        />
                      </div>
                      <div>
                        <Label>Contact Phone</Label>
                        <Input
                          value={editForm.contact2_phone || ""}
                          onChange={(e) => setEditForm({ ...editForm, contact2_phone: e.target.value })}
                          placeholder="(555) 987-6543"
                          data-testid="edit-contact2-phone"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Input
                      value={editForm.address || ""}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      data-testid="edit-address"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={editForm.city || ""}
                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        data-testid="edit-city"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Select
                        value={editForm.state || ""}
                        onValueChange={(value) => setEditForm({ ...editForm, state: value })}
                      >
                        <SelectTrigger data-testid="edit-state">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_placeholder" disabled>Select state</SelectItem>
                          {US_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>ZIP</Label>
                      <Input
                        value={editForm.zip_code || ""}
                        onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })}
                        data-testid="edit-zip"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editForm.phone || ""}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        data-testid="edit-phone"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={editForm.email || ""}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        data-testid="edit-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={editForm.website || ""}
                      onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                      data-testid="edit-website"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={editForm.status || ""}
                      onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                    >
                      <SelectTrigger data-testid="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_placeholder" disabled>Select status</SelectItem>
                        {Object.entries(statusLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assigned To</Label>
                    <Select
                      value={editForm.assigned_to || ""}
                      onValueChange={(value) => setEditForm({ ...editForm, assigned_to: value })}
                    >
                      <SelectTrigger data-testid="edit-assigned">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_unassigned" disabled>Unassigned</SelectItem>
                        {users.map(user => (
                          <SelectItem key={user.user_id} value={user.user_id}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={editForm.notes || ""}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      data-testid="edit-notes"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Contact Person */}
                  {(lead.contact_name || lead.contact_email || lead.contact_phone) && (
                    <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100 space-y-2" data-testid="contact-info-section">
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider flex items-center gap-1.5">
                        <UserCircle className="w-3.5 h-3.5" />
                        Primary Contact
                      </p>
                      {lead.contact_name && (
                        <p className="text-slate-900 font-medium">{lead.contact_name}</p>
                      )}
                      <div className="flex items-center gap-4 flex-wrap">
                        {lead.contact_email && (
                          <a href={`mailto:${lead.contact_email}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {lead.contact_email}
                          </a>
                        )}
                        {lead.contact_phone && (
                          <a href={`tel:${lead.contact_phone}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {lead.contact_phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Secondary Contact */}
                  {(lead.contact2_name || lead.contact2_email || lead.contact2_phone) && (
                    <div className="p-3 rounded-lg bg-violet-50/50 border border-violet-100 space-y-2" data-testid="contact2-info-section">
                      <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider flex items-center gap-1.5">
                        <UserCircle className="w-3.5 h-3.5" />
                        Secondary Contact
                      </p>
                      {lead.contact2_name && (
                        <p className="text-slate-900 font-medium">{lead.contact2_name}</p>
                      )}
                      <div className="flex items-center gap-4 flex-wrap">
                        {lead.contact2_email && (
                          <a href={`mailto:${lead.contact2_email}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {lead.contact2_email}
                          </a>
                        )}
                        {lead.contact2_phone && (
                          <a href={`tel:${lead.contact2_phone}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {lead.contact2_phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {lead.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-900">{lead.address}</p>
                        <p className="text-slate-500 text-sm">
                          {lead.city}, {lead.state} {lead.zip_code}
                        </p>
                      </div>
                    </div>
                  )}
                  {lead.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                        {lead.phone}
                      </a>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                        {lead.email}
                      </a>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-slate-400" />
                      <a 
                        href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {lead.website}
                      </a>
                    </div>
                  )}
                  {lead.social_links && Object.keys(lead.social_links).length > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-xs uppercase tracking-wide w-5">Soc</span>
                      <SocialLinks links={lead.social_links} size={16} />
                    </div>
                  )}
                  {/* Timestamped notes log (chronological) */}
                  <NotesLog
                    leadId={leadId}
                    legacyNotes={lead.notes}
                    legacyNotesUpdatedAt={lead.notes_updated_at}
                    entries={lead.notes_entries || []}
                    onChanged={fetchLead}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Communication History - Tabs for Calls and Emails */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Files & Attachments</CardTitle>
            </CardHeader>
            <CardContent>
              <FileAttachments entityType="lead" entityId={lead?.lead_id} />
            </CardContent>
          </Card>

          {/* Communication History */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Communication History</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="calls">
                <TabsList className="mb-4">
                  <TabsTrigger value="calls">
                    <Phone className="w-4 h-4 mr-2" />
                    Calls ({calls.length})
                  </TabsTrigger>
                  <TabsTrigger value="emails">
                    <Mail className="w-4 h-4 mr-2" />
                    Emails ({emails.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="calls">
                  {calls.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Phone className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No calls logged yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {calls.map((call) => {
                        const outcomeInfo = callOutcomes.find(o => o.value === call.outcome);
                        const OutcomeIcon = outcomeInfo?.icon || CheckCircle2;
                        return (
                          <div key={call.call_id} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <OutcomeIcon className={`w-4 h-4 ${outcomeInfo?.color}`} />
                                <span className="font-medium">{outcomeInfo?.label || call.outcome}</span>
                              </div>
                              <span className="text-sm text-slate-500">{formatDate(call.call_time)}</span>
                            </div>
                            <div className="mt-2 flex items-center gap-4 text-sm text-slate-500">
                              <span>by {call.user_name}</span>
                              <span>{formatDuration(call.duration_seconds)}</span>
                            </div>
                            {call.notes && (
                              <p className="mt-2 text-sm text-slate-600">{call.notes}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="emails">
                  {emails.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Mail className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No emails sent yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {emails.map((email) => (
                        <div key={email.email_id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Send className="w-4 h-4 text-blue-500" />
                              <span className="font-medium">{email.subject}</span>
                              {email.opened_at && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-green-100 text-green-700"
                                  title={`First opened ${formatDate(email.opened_at)}${email.open_count > 1 ? ` · ${email.open_count} total opens` : ""}`}
                                  data-testid={`opened-${email.email_id}`}
                                >
                                  <Eye className="w-3 h-3" />
                                  Opened{email.open_count > 1 ? ` ×${email.open_count}` : ""}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-slate-500">{formatDate(email.sent_at)}</span>
                          </div>
                          <div className="mt-2 text-sm text-slate-500">
                            <span>by {email.user_name} → {email.to_email}</span>
                          </div>
                          <p className="mt-2 text-sm text-slate-600 line-clamp-2">{email.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <Badge className={`${statusColors[lead.status]} mt-1`}>
                  {statusLabels[lead.status]}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Assigned To</p>
                <p className="text-slate-900">
                  {lead.assigned_to 
                    ? users.find(u => u.user_id === lead.assigned_to)?.name || "Unknown"
                    : "Unassigned"
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Source</p>
                <p className="text-slate-900 capitalize">{lead.source || "Manual"}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-slate-900">{calls.length}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Emails</p>
                <p className="text-slate-900">{emails.length}</p>
              </div>
              {lead.last_contacted_at && (
                <div>
                  <p className="text-sm text-slate-500">Last Contacted</p>
                  <p className="text-slate-900">{formatDate(lead.last_contacted_at)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {lead.rating && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Rating</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < Math.round(lead.rating) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
                    />
                  ))}
                  <span className="ml-2 text-slate-600">{lead.rating?.toFixed(1)}</span>
                </div>
                {lead.user_ratings_total && (
                  <p className="text-sm text-slate-500 mt-1">{lead.user_ratings_total} reviews</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
